/* eslint-disable complexity */
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useCallback } from 'react'
import { Schemas } from '~/api/gen'
import { NoResults } from '~/components/elements/NoResults'
import { MemberHeader } from '~/components/modules/Header/MemberHeader/MemberHeader'
import { MemberHeaderForPc } from '~/components/modules/Header/MemberHeader/MemberHeaderForPc'
import { DeprecatedFollowButton } from '~/components/modules/Header/MemberHeader/components/DeprecatedFollowButton'
import { HeadlineForPc } from '~/components/modules/HeadlineForPc/HeadlineForPc'
import { MoreHorizPopup } from '~/components/modules/MoreHorizPopup/MoreHorizPopup'
import { Pagination } from '~/components/modules/Pagination/Pagination'
import { ShareButtons } from '~/components/modules/ShareButtons'
import { SpamCategories } from '~/components/modules/SpamModal/SpamCategories'
import { useIsLoggedIn } from '~/hooks/useIsLoggedIn'
import { useIsLoginMember } from '~/hooks/useIsLoginMember'
import { useMainHeaderShadowVisibilityForPcActions } from '~/hooks/useMainHeaderShadowVisibilityForPc'
import { useMember } from '~/hooks/useMember'
import { useModal } from '~/hooks/useModal'
import { usePaginationProps } from '~/hooks/usePaginationProps'
import { useSnsShareUrl } from '~/hooks/useSnsShareUrl'
import { useSubHeaderFloating } from '~/hooks/useSubHeaderFloating'
import { NEXT_PUBLIC_WEAR2_WEB_URL } from '~/lib/env'
import { ActionButton } from './components/ActionButton'
import { FilterSelector } from './components/FilterSelector'
import { FolderElementList } from './components/FolderElementList'
import { FolderElementListItem } from './components/FolderElementListItem'
import { ItemCounter } from './components/ItemCounter'
import { TagList } from './components/TagList'
import { UpdateDate } from './components/UpdateDate'
import { ViewCounter } from './components/ViewCounter'
import { FETCH_COUNT_PER_PAGE } from './constants'
import { footer, pcTopAd, pageBottomPcAds } from './data/ad'
import {
  FilterName,
  filterNameTypeMap,
  FilterType,
  filterTypeNameMap,
  filterTypeValueMap,
} from './lib/filter'

const GoogleAd = dynamic(
  () => import('~/features/shared/ad/components/GoogleAd'),
  {
    ssr: false,
  }
)

export type Props = {
  detail: Schemas.folder_detail
  folderElements: Schemas.folder_element_detail[]
  maxCount: number
  pageNumber: number
  filterType: FilterType
}

const ThinBannerForSP = dynamic(
  () => import('~/features/shared/banner/SP/ThinBanner'),
  {
    ssr: false,
  }
)

const ThinBannerForPC = dynamic(
  () => import('~/features/shared/banner/PC/ThinBanner'),
  { ssr: false }
)

const SpamModal = dynamic(
  () =>
    import('~/components/modules/SpamModal/SpamModal').then((mod) => ({
      default: mod.SpamModal,
    })),
  {
    ssr: false,
  }
)

const filterValues = new Set<FilterName>(Object.values(filterTypeNameMap))

const getElementCount = (
  aggregate: Exclude<Schemas.folder_aggregate, null>,
  filterType: FilterType
): number => {
  switch (filterType) {
    case 'ALL':
      return aggregate.active_element_count
    case 'COORDINATE':
      return aggregate.active_coordinate_count
    case 'ITEM':
      return aggregate.active_item_count
  }
}

const noResultsMessages = {
  ALL: 'お気に入りがありません',
  COORDINATE: 'コーディネートがありません',
  ITEM: 'アイテムがありません',
} as const

export const FolderDetail: React.FC<Props> = ({
  detail,
  folderElements,
  maxCount,
  pageNumber,
  filterType,
}) => {
  const { push, query } = useRouter()
  const { subHeaderRef: pcSubHeaderRef, floating: pcSubHeaderFloating } =
    useSubHeaderFloating()
  const { showPcShadow, hidePcShadow } =
    useMainHeaderShadowVisibilityForPcActions()
  const username = detail.member.user_name
  const isLoginMemberContent = useIsLoginMember(username)
  const member = useMember(username) ?? {
    ...detail.member,
    age: null,
    is_age_published: false,
  }

  if (pcSubHeaderFloating) {
    hidePcShadow()
  } else {
    showPcShadow()
  }

  const onFilterChange = useCallback(
    (filter: FilterName) => {
      push({
        pathname: '/[username]/favorite/[folderId]',
        query: {
          ...query,
          dp_type: filterTypeValueMap[filterNameTypeMap[filter]],
        },
      })
    },
    [push, query]
  )
  const totalPage = Math.max(
    1,
    Math.ceil(
      Math.min(
        detail?.aggregate ? getElementCount(detail.aggregate, filterType) : 1,
        maxCount ?? 0
      ) / FETCH_COUNT_PER_PAGE
    )
  )
  const paginationProps = usePaginationProps(pageNumber, totalPage, false)

  const [ReportModal, openReportModal, closeReportModal, reportModalIsOpen] =
    useModal(300)
  const isLoggedIn = useIsLoggedIn()

  const snsShareUrls = useSnsShareUrl({
    shareUrl: `${NEXT_PUBLIC_WEAR2_WEB_URL}/${username}/favorite/${detail.id}/`,
    utmCampaign: `SaveID_${detail.id}`,
    hashtags: ['WEARコーデ'],
  })

  return (
    <>
      <div className="xl:hidden">
        <ThinBannerForSP bannerType="sp_narrow_menu_other" />
      </div>
      <div className="hidden xl:block">
        <ThinBannerForPC bannerType="pc_gbl_ad_other" />
      </div>
      <div className="hidden xl:block">
        <HeadlineForPc>
          <MoreHorizPopup>
            <div className="py-[7px]">
              <ShareButtons
                twitterShareUrl={snsShareUrls.twitterShareUrl}
                facebookShareUrl={snsShareUrls.facebookShareUrl}
              />
            </div>
            {isLoggedIn && !isLoginMemberContent && (
              <button
                onClick={openReportModal}
                className="w-full border-t border-gray-300 py-3 text-[11px] leading-[1.4]"
                type="button"
              >
                このフォルダを通報する
              </button>
            )}
          </MoreHorizPopup>
          <ReportModal
            className={`transition-opacity duration-200 ease-linear ${
              reportModalIsOpen ? 'opacity-100' : 'opacity-0'
            }`}
            overlayClassName={`ease-linear duration-200 transition-opacity bg-black/60 ${
              reportModalIsOpen ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <SpamModal
              type={SpamCategories.Folder}
              close={closeReportModal}
              targetId={detail.id}
            />
          </ReportModal>
        </HeadlineForPc>
      </div>

      <div className="xl:hidden">
        <MemberHeader member={detail.member} hasBottomShadow={false}>
          <ActionButton folderId={detail.id} userName={username} />
        </MemberHeader>
      </div>
      <div
        className="sticky top-[54px] z-contentHeader hidden xl:block xl:[&>*:first-child]:border-b-0"
        ref={pcSubHeaderRef}
      >
        <MemberHeaderForPc
          member={member}
          hasBottomShadow={pcSubHeaderFloating}
          linkUrl={member ? `/${member.user_name}/` : undefined}
          linkLabel={member ? `${member.nick_name}のページへ` : undefined}
        >
          {member !== undefined && !isLoginMemberContent && (
            <DeprecatedFollowButton member={member} />
          )}
        </MemberHeaderForPc>
      </div>

      <section className="px-3 pt-[9px] xl:border-b xl:border-b-gray-300 xl:bg-white xl:px-0 xl:pb-[5px]">
        <div className="xl:relative xl:mx-auto xl:w-[990px]">
          <h1 className="text-[20px] font-bold leading-[1.2] xl:text-[22px]">
            {detail.name}
          </h1>
          <div className="flex gap-x-2 pb-1 pt-[6px] xl:pt-3">
            <ItemCounter count={detail.aggregate?.active_element_count ?? 0} />
            <ViewCounter count={detail.aggregate?.view_count ?? 0} />
          </div>
          <p className="whitespace-pre-line pt-[10px] text-[13px] leading-normal xl:pt-3 xl:text-[14px] xl:leading-[1.6]">
            {detail.note}
          </p>
          <div className="pt-[7px] xl:pt-[18px]">
            <TagList tags={detail.coordinate_tags} />
          </div>
          <div className="xl:flex xl:justify-between">
            <div className="pb-[1px] pt-3 xl:pb-[15px] xl:pt-5">
              <UpdateDate updatedAt={detail.updated_at} />
            </div>
            <div className="py-[14px] xl:flex xl:items-end xl:p-0">
              <FilterSelector
                values={filterValues}
                value={filterTypeNameMap[filterType]}
                onChange={onFilterChange}
              />
            </div>
          </div>
        </div>
      </section>
      <div className="hidden justify-center xl:flex">
        <GoogleAd elementId={pcTopAd} className="xl:pt-5 xl:empty:pt-0" />
      </div>
      {folderElements.length > 0 ? (
        <div className="xl:mx-auto xl:w-[990px]">
          <section className="px-3 pb-6 xl:px-0 xl:pb-[50px] xl:pt-5">
            <FolderElementList>
              {folderElements.map((element, i) => (
                <FolderElementListItem
                  key={
                    element.folder_coordinate?.id ??
                    element.folder_item?.item.id ??
                    i
                  }
                  folderCoordinate={element.folder_coordinate}
                  folderItem={element.folder_item}
                  imagePriority={i < 6}
                />
              ))}
            </FolderElementList>
          </section>
          <Pagination {...paginationProps} />
          <div className="pt-5 xl:hidden">
            <div className="h-4 bg-gray-50 shadow-[inset_0_7px_5px_-6px_rgb(0_0_0/10%)]" />
          </div>
        </div>
      ) : (
        <NoResults className="pb-[30px] pt-9" type="FOLDER">
          {noResultsMessages[filterType]}
        </NoResults>
      )}
      <div className="bg-white pt-[25px] xl:hidden">
        <GoogleAd elementId={footer} />
      </div>
      <div className="hidden xl:flex xl:justify-center xl:gap-[50px] xl:pt-[60px]">
        {pageBottomPcAds.map((ad) => (
          <GoogleAd key={ad} elementId={ad} />
        ))}
      </div>
    </>
  )
}
